import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "./icons";
import { timeLabel } from "../../../lib/artemis/present";
import {
  alertKindLabel, channelsAfter, channelsLabel, checkedLine, deviceTimeZone, emailOffText, focusAfter, focusOptions, focusPicked,
  leadDaysLabel, offeredChannels, parseLeadDays, quietHoursError, settingsPayload, statusTag, timeZoneOptions
} from "../../../lib/artemis/alerts";

// The alert settings screen (WEB-7145, API-3840): the user's alerts, to pause, change or delete, and
// their defaults (channels, time zone, quiet hours). Alerts are set up in chat, so there's no Add.

// { available, loaded, items, live, limit, conversationId, settings, includeFinished, error, refresh,
// setIncludeFinished, update, remove, saveSettings }. api holds the dispatched Alerts actions.
// available is false where the server has no alerts (every route a 404: Production for now).
export const useAlerts = api => {
  const [available, setAvailable] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [page, setPage] = useState({ Items: [], Live: 0, Limit: 25, AlertsConversationId: null })
  const [settings, setSettings] = useState(null)
  const [includeFinished, setIncludeFinished] = useState(false)
  const [error, setError] = useState(null)
  const finishedRef = useRef(includeFinished)
  finishedRef.current = includeFinished

  const refresh = useCallback(() => Promise.all([api.list(finishedRef.current), api.settings()])
    .then(([list, prefs]) => {
      setPage({ Items: [], Live: 0, Limit: 25, AlertsConversationId: null, ...(list || {}) })
      setSettings(prefs || null)
      setAvailable(true)
      setLoaded(true)
      setError(null)
    })
    .catch(err => {
      if (err && (err.status === 404 || err.code === 'http_404')) setAvailable(false)
      else setError('Couldn’t load your alerts')
      setLoaded(true)
    }), [api])

  useEffect(() => { refresh() }, [refresh, includeFinished])

  const update = useCallback((id, patch) => api.update(id, patch).then(saved => {
    setPage(p => ({ ...p, Items: p.Items.map(a => (a.Id === id ? (saved || { ...a, ...patch }) : a)) }))
    // Pausing and resuming change how many are live.
    if (patch.Paused !== undefined) api.list(finishedRef.current).then(list => list && setPage(p => ({ ...p, Live: list.Live, Limit: list.Limit }))).catch(() => {})
    return saved
  }), [api])
  const remove = useCallback(id => api.remove(id).then(() => setPage(p => ({ ...p, Items: p.Items.filter(a => a.Id !== id), Live: Math.max(0, p.Live - 1) }))), [api])
  const saveSettings = useCallback(body => api.saveSettings(body).then(saved => { setSettings(saved); return saved }), [api])

  return {
    available, loaded, items: page.Items || [], live: page.Live, limit: page.Limit, conversationId: page.AlertsConversationId,
    settings, includeFinished, setIncludeFinished, error, refresh, update, remove, saveSettings
  }
}

const errorText = (err, fallback) => (err && err.userMessage) || fallback

export const Switch = ({ on, label, onChange, disabled }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
    className="artemis-switch-button" onClick={() => onChange(!on)}>
    <span className={`artemis-switch ${on ? 'artemis-switch-on' : ''}`} aria-hidden="true"><span /></span>
  </button>
)

// Checkboxes for the offered channels; the inbox is always on.
const ChannelPicks = ({ offered, picked, onChange, label }) => (
  <fieldset className="artemis-alert-channels">
    <legend className="artemis-field-label">{label}</legend>
    {offered.map(c => (
      <label key={c.key} className="artemis-alert-check">
        <input type="checkbox" checked={c.fixed || picked.includes(c.key)} disabled={c.fixed}
          onChange={e => onChange(e.target.checked ? [...picked, c.key] : picked.filter(k => k !== c.key))} />
        <span>{c.label}{c.fixed ? ' (always)' : ''}</span>
      </label>
    ))}
  </fieldset>
)

// What a change watch follows (WEB-7167): season dates, application dates, the booklet, unit warnings.
const FocusPicks = ({ hasUnit, picked, onChange }) => (
  <fieldset className="artemis-alert-channels">
    <legend className="artemis-field-label">Tell me about</legend>
    {focusOptions(hasUnit).map(f => (
      <label key={f.key} className="artemis-alert-check">
        <input type="checkbox" checked={picked.includes(f.key)}
          onChange={e => onChange(e.target.checked ? [...picked, f.key] : picked.filter(k => k !== f.key))} />
        <span>{f.label}</span>
      </label>
    ))}
  </fieldset>
)

// Change when it reminds (a deadline) or what it follows (a change watch), and where it goes.
export const AlertEditor = ({ alert, offered, onSave, onCancel }) => {
  const watch = alert.Kind === 'change'
  const hasUnit = !!alert.Unit
  const [leads, setLeads] = useState((alert.LeadDays || []).join(', '))
  const [focus, setFocus] = useState(() => focusPicked(alert.Focus, hasUnit))
  const [picked, setPicked] = useState(alert.Channels || ['inbox'])
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)
  const parsed = watch ? { days: [] } : parseLeadDays(leads)
  const focusRead = watch ? focusAfter(focus, hasUnit) : {}
  const invalid = !!(parsed.error || focusRead.error)
  const save = () => {
    if (invalid) return
    const patch = {}
    if (watch) {
      if (focusRead.focus.join() !== focusAfter(focusPicked(alert.Focus, hasUnit), hasUnit).focus.join()) patch.Focus = focusRead.focus
    } else if (parsed.days.join() !== [...(alert.LeadDays || [])].sort((a, b) => b - a).join()) patch.LeadDays = parsed.days
    const channels = channelsAfter(alert.Channels, offered, picked)
    if (channels.join() !== (alert.Channels || []).join()) patch.Channels = channels
    if (Object.keys(patch).length === 0) return onCancel()
    setSaving(true)
    setServerError(null)
    onSave(patch).catch(err => setServerError(errorText(err, 'Couldn’t save that. Try again.'))).finally(() => setSaving(false))
  }
  return (
    <div className="artemis-alert-editor">
      {watch ? (
        <>
          <FocusPicks hasUnit={hasUnit} picked={focus} onChange={setFocus} />
          {focusRead.error && <span className="artemis-field-error">{focusRead.error}</span>}
        </>
      ) : (
        <label className="artemis-field">
          <span className="artemis-field-label">Remind me (days before)</span>
          <span className="artemis-field-hint">{parsed.error ? null : leadDaysLabel(parsed.days)}</span>
          <input className="artemis-input-field" value={leads} onChange={e => setLeads(e.target.value)} placeholder="14, 1" aria-invalid={!!parsed.error} />
          {parsed.error && <span className="artemis-field-error">{parsed.error}</span>}
        </label>
      )}
      <ChannelPicks offered={offered} picked={picked} onChange={setPicked} label="Send to" />
      {serverError && <p className="artemis-field-error" role="alert">{serverError}</p>}
      <div className="artemis-skills-actions">
        <button type="button" className="artemis-small-button" onClick={onCancel}>Cancel</button>
        <button type="button" className="artemis-small-button artemis-small-button-go" disabled={saving || invalid} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  )
}

const AlertRow = ({ alert, offered, focused, onUpdate, onRemove, onOpenLast }) => {
  const [mode, setMode] = useState(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const ref = useRef(null)
  useEffect(() => { if (focused && ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ block: 'center' }) }, [focused])
  const tag = statusTag(alert.Status)
  const running = alert.Status === 'active' || alert.Status === 'paused'
  const run = (patch, fallback) => {
    setBusy(true)
    setProblem(null)
    return onUpdate(alert.Id, patch).catch(err => setProblem(errorText(err, fallback))).finally(() => setBusy(false))
  }

  if (mode === 'delete') {
    return (
      <div className="artemis-card-button artemis-card-confirm" role="group" aria-label={`Delete ${alert.Title}`} ref={ref}>
        <span className="artemis-flex">Delete “{alert.Title}”?</span>
        <button type="button" className="artemis-small-button" onClick={() => setMode(null)}>Cancel</button>
        <button type="button" className="artemis-small-button artemis-small-button-danger" disabled={busy}
          onClick={() => { setBusy(true); onRemove(alert.Id).catch(err => { setProblem(errorText(err, 'Couldn’t delete it. Try again.')); setMode(null) }).finally(() => setBusy(false)) }}>Delete</button>
      </div>
    )
  }
  return (
    <div className={`artemis-alert-row ${alert.Status === 'paused' ? 'artemis-skill-off' : ''} ${focused ? 'artemis-alert-highlight' : ''}`} ref={ref}>
      <div className="artemis-alert-row-main">
        <span className="artemis-card-icon"><Icon name="bell" size={16} /></span>
        <span className="artemis-card-text">
          <span className="artemis-card-title">
            {alert.Title}
            {alertKindLabel(alert.Kind) && <span className="artemis-hunt-badge">{alertKindLabel(alert.Kind)}</span>}
            {tag && <span className="artemis-hunt-badge artemis-badge-off">{tag}</span>}
          </span>
          {alert.Description && <span className="artemis-card-sub">{alert.Description}</span>}
          {checkedLine(alert) && <span className="artemis-card-sub">{checkedLine(alert)}</span>}
          <span className="artemis-card-sub">To: {channelsLabel(alert.Channels)}</span>
          {alert.LastMessage && (
            <button type="button" className="artemis-link-button artemis-alert-last" onClick={() => onOpenLast(alert.LastMessage)}>
              Last alert {timeLabel(Date.parse(alert.LastMessage.SentAt))}
            </button>
          )}
          {alert.LastError && <span className="artemis-field-error">{alert.LastError}</span>}
        </span>
        {running
          ? <Switch on={alert.Status === 'active'} label={`${alert.Status === 'active' ? 'Pause' : 'Resume'} ${alert.Title}`} disabled={busy}
              onChange={on => run({ Paused: !on }, 'Couldn’t change it. Try again.')} />
          : <button type="button" className="artemis-small-button" disabled={busy} onClick={() => run({ Paused: false }, 'Couldn’t resume it. Try again.')}>Resume</button>}
        <button type="button" className="artemis-icon-button" title="Edit" aria-label={`Edit ${alert.Title}`} aria-expanded={mode === 'edit'}
          onClick={() => setMode(m => (m === 'edit' ? null : 'edit'))}><Icon name="edit" size={16} /></button>
        <button type="button" className="artemis-icon-button" title="Delete" aria-label={`Delete ${alert.Title}`} onClick={() => setMode('delete')}><Icon name="trash" size={16} /></button>
      </div>
      {problem && <p className="artemis-field-error" role="alert">{problem}</p>}
      {mode === 'edit' && <AlertEditor alert={alert} offered={offered} onCancel={() => setMode(null)} onSave={patch => onUpdate(alert.Id, patch).then(() => setMode(null))} />}
    </div>
  )
}

const formFrom = settings => ({
  channels: (settings && settings.Channels) || ['inbox'],
  timeZone: (settings && settings.TimeZone) || deviceTimeZone() || '',
  quietStart: (settings && settings.QuietHoursStart) || '',
  quietEnd: (settings && settings.QuietHoursEnd) || ''
})

// Defaults for alerts set up later, and the user's own time zone and quiet hours (which every alert takes).
export const AlertDefaults = ({ settings, offered, onSave }) => {
  const [form, setForm] = useState(() => formFrom(settings))
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  useEffect(() => { setForm(formFrom(settings)) }, [settings])
  const zones = timeZoneOptions(form.timeZone)
  const quietError = quietHoursError(form.quietStart, form.quietEnd)
  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }))
  const save = (extra = {}) => {
    if (quietError) return
    setSaving(true)
    setMessage(null)
    onSave({ ...settingsPayload({ ...form, channels: channelsAfter(settings && settings.Channels, offered, form.channels) }), ...extra })
      .then(() => setMessage({ ok: true, text: 'Saved' }))
      .catch(err => setMessage({ ok: false, text: errorText(err, 'Couldn’t save your settings. Try again.') }))
      .finally(() => setSaving(false))
  }
  const emailOff = settings && emailOffText(settings.EmailOff)
  return (
    <section className="artemis-alert-defaults" aria-label="Alert settings">
      <span className="artemis-eyebrow">Settings</span>
      {emailOff && (
        <p className="artemis-dictation-note" role="status">
          {emailOff} <button type="button" className="artemis-link-button" disabled={saving} onClick={() => save({ EmailOn: true })}>Turn emails back on</button>
        </p>
      )}
      <ChannelPicks offered={offered} picked={form.channels} onChange={channels => setForm(f => ({ ...f, channels }))} label="New alerts go to" />
      <label className="artemis-field">
        <span className="artemis-field-label">Time zone</span>
        <span className="artemis-field-hint">Reminders say the time here. Every alert uses it.</span>
        <select className="artemis-input-field" value={form.timeZone} onChange={set('timeZone')}>
          {zones.map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
        </select>
      </label>
      <div className="artemis-field">
        <span className="artemis-field-label">Quiet hours</span>
        <span className="artemis-field-hint">Phone alerts wait until quiet hours end. Your alerts inbox and email still get them.</span>
        <span className="artemis-alert-quiet">
          <input className="artemis-input-field" type="time" value={form.quietStart} onChange={set('quietStart')} aria-label="Quiet hours start" />
          <span>to</span>
          <input className="artemis-input-field" type="time" value={form.quietEnd} onChange={set('quietEnd')} aria-label="Quiet hours end" />
          {(form.quietStart || form.quietEnd) && (
            <button type="button" className="artemis-link-button" onClick={() => setForm(f => ({ ...f, quietStart: '', quietEnd: '' }))}>None</button>
          )}
        </span>
        {quietError && <span className="artemis-field-error">{quietError}</span>}
      </div>
      <div className="artemis-skills-actions">
        {message && <span className={message.ok ? 'artemis-muted' : 'artemis-field-error'} role="status">{message.text}</span>}
        <span className="artemis-flex" />
        <button type="button" className="artemis-small-button artemis-small-button-go" disabled={saving || !!quietError} onClick={() => save()}>{saving ? 'Saving…' : 'Save settings'}</button>
      </div>
    </section>
  )
}

// The screen. focusId is an alert to scroll to (from an alert card's Manage). onOpenLast opens an
// alert's last message in the alerts conversation.
export const AlertsView = ({ alerts, focusId, onBack, onOpenLast, onAsk }) => {
  const offered = offeredChannels(alerts.settings && alerts.settings.AvailableChannels)
  return (
    <>
      <div className="artemis-view-title">
        <button type="button" className="artemis-icon-button" title="Back" aria-label="Back" onClick={onBack}><Icon name="back" size={18} /></button>
        <h2>Your alerts</h2>
      </div>
      <p className="artemis-muted">
        Artemis reminds you before draw deadlines and watches hunts for changes. Set one up in chat. {alerts.loaded && alerts.available ? `${alerts.live} of ${alerts.limit} in use.` : ''}
      </p>
      {alerts.error && <p className="artemis-muted" role="status">{alerts.error}. <button type="button" className="artemis-link-button" onClick={alerts.refresh}>Try again</button></p>}
      {!alerts.loaded && <p className="artemis-muted" role="status">Loading your alerts…</p>}
      <div className="artemis-card-list">
        {alerts.loaded && alerts.items.length === 0 && !alerts.error && (
          <div className="artemis-alert-empty">
            <p className="artemis-muted">{alerts.includeFinished ? 'No alerts yet.' : 'No active alerts.'}</p>
            <button type="button" className="artemis-follow-up" onClick={() => onAsk('Remind me 2 weeks before my next draw application closes')}>
              Remind me 2 weeks before my next draw application closes
            </button>
          </div>
        )}
        {alerts.items.map(a => (
          <AlertRow key={a.Id} alert={a} offered={offered} focused={a.Id === focusId} onUpdate={alerts.update} onRemove={alerts.remove} onOpenLast={onOpenLast} />
        ))}
        {alerts.loaded && (
          <label className="artemis-alert-check artemis-alert-finished">
            <input type="checkbox" checked={alerts.includeFinished} onChange={e => alerts.setIncludeFinished(e.target.checked)} />
            <span>Show finished alerts</span>
          </label>
        )}
      </div>
      {alerts.settings && <AlertDefaults settings={alerts.settings} offered={offered} onSave={alerts.saveSettings} />}
    </>
  )
}
